import apiClient from '@/shared/api/client'

const BASE = '/purchase/contracts'

export interface TCTemplate {
  id: string
  name: string
  category: string
  text: string
  is_active: boolean
  created_at: string
}

export interface CottonContractSpec {
  id: string
  po_id: string
  trade_type: 'DOMESTIC' | 'EXPORT'
  dispatch_type: 'DIRECT' | 'PARAKH'
  quantity_bales: number
  avg_bale_wt_kg: number
  rate_unit: 'CANDY' | 'CENTS_PER_LB' | 'PER_KG'
  rate_value: number
  candy_weight_kg: number
  rate_per_kg: number
  cotton_variety: string | null
  crop_year: string | null
  origin: string | null
  mic_min: number | null
  mic_max: number | null
  staple_length_mm_min: number | null
  staple_length_mm_max: number | null
  strength_min: number | null
  uniformity_min: number | null
  short_fiber_pct_max: number | null
  trash_pct_max: number | null
  moisture_pct_max: number | null
  color_rd_min: number | null
  color_b_max: number | null
  elongation_min: number | null
  sci_min: number | null
  quality_remarks: string | null
  tc_template_id: string | null
  tc_text: string | null
  broker_id: string | null
  broker_type: string | null           // RM_BROKER / FG_BROKER
  broker_name: string | null
  broker_email: string | null
  broker_commission_type: string | null  // PCT / FIXED
  broker_commission_pct: number | null
  broker_commission_fixed: number | null
  created_at: string
  updated_at: string
}

export interface MoistureReading {
  bale_no: string
  moisture_pct: number
}

export interface ParakhLot {
  id: string
  po_id: string | null
  ginner_lot_no: string
  pr_no: string | null
  total_bales: number | null
  sample_size: number | null
  moisture_readings: MoistureReading[] | null
  avg_moisture: number | null
  moisture_pct: number | null
  sample_date: string | null
  ginner_name: string | null
  ginner_location: string | null
  ginner_sign_date: string | null
  tested_at: string | null      // MILL_LAB / EXTERNAL_LAB
  test_result: string           // PENDING / PASS / FAIL
  test_date: string | null
  mic_value: number | null
  test_remarks: string | null
  hvi_results: Record<string, number | null> | null
  lab_file_name: string | null
  // Trial sample fields
  sample_type?: string          // 'PARAKH' | 'TRIAL'
  trial_ref_no?: string | null
  cotton_variety?: string | null
  crop_year?: string | null
  contact_person?: string | null
  contact_phone?: string | null
  expected_qty_bales?: number | null
  price_per_candy_rs?: number | null
  purchase_recommendation?: string | null  // PENDING/RECOMMENDED/DO_NOT_BUY/NEED_MORE_SAMPLES
  plant_id?: string | null
  // QC all-lots view fields (populated by /parakh-lots/all or /trial-samples)
  po_no?: string | null
  po_status?: string | null
  supplier_name?: string | null
  // Phase COTTON-PARAKH-002 — HVI sample count + retest chain
  hvi_sample_bales_count?: number | null
  retest_of_lot_id?: string | null
  retest_reason?: string | null
  retest_requested_by?: string | null
  retest_requested_at?: string | null
  created_at: string
  updated_at: string
}

export interface ParsedLabResult {
  mic_value: number | null
  staple_length_mm: number | null
  strength: number | null
  uniformity: number | null
  short_fiber_pct: number | null
  trash_pct: number | null
  moisture_pct: number | null
  color_rd: number | null
  color_b: number | null
  elongation: number | null
  sci: number | null
  raw_text: string | null
}

export const cottonContractApi = {
  listTemplates: (activeOnly = true) =>
    apiClient.get<TCTemplate[]>(`${BASE}/tc-templates`, { params: { active_only: activeOnly } }).then(r => r.data),
  createTemplate: (data: Partial<TCTemplate>) =>
    apiClient.post<TCTemplate>(`${BASE}/tc-templates`, data).then(r => r.data),
  updateTemplate: (id: string, data: Partial<TCTemplate>) =>
    apiClient.patch<TCTemplate>(`${BASE}/tc-templates/${id}`, data).then(r => r.data),
  getSpec: (poId: string) =>
    apiClient.get<CottonContractSpec>(`${BASE}/${poId}/spec`).then(r => r.data),
  upsertSpec: (poId: string, data: Partial<CottonContractSpec>) =>
    apiClient.put<CottonContractSpec>(`${BASE}/${poId}/spec`, data).then(r => r.data),
  getPdfUrl: (poId: string) => `/api/v1/purchase/contracts/${poId}/pdf`,
  sendEmail: (poId: string, payload: { to_emails: string[]; subject: string; body: string }) =>
    apiClient.post(`${BASE}/${poId}/send-email`, payload).then(r => r.data),

  // Parakh lots
  listParakhLots: (poId: string) =>
    apiClient.get<ParakhLot[]>(`${BASE}/${poId}/parakh-lots`).then(r => r.data),
  createParakhLot: (poId: string, data: Partial<ParakhLot>) =>
    apiClient.post<ParakhLot>(`${BASE}/${poId}/parakh-lots`, data).then(r => r.data),
  updateParakhLot: (poId: string, lotId: string, data: Partial<ParakhLot>) =>
    apiClient.patch<ParakhLot>(`${BASE}/${poId}/parakh-lots/${lotId}`, data).then(r => r.data),
  deleteParakhLot: (poId: string, lotId: string) =>
    apiClient.delete(`${BASE}/${poId}/parakh-lots/${lotId}`).then(r => r.data),
  parseLabFile: (poId: string, lotId: string, fileName: string, fileData: string) =>
    apiClient.post<ParsedLabResult>(
      `${BASE}/${poId}/parakh-lots/${lotId}/parse-results`,
      { file_name: fileName, file_data: fileData }
    ).then(r => r.data),

  // Standalone parse — no lot_id needed (for new lots before save)
  parseLabFileStandalone: (fileName: string, fileData: string) =>
    apiClient.post<ParsedLabResult>(`${BASE}/parse-lab-file`, {
      file_name: fileName, file_data: fileData,
    }).then(r => r.data),

  // QC view — all parakh lots across POs
  listAllParakhLots: (plantId?: string) =>
    apiClient.get<ParakhLot[]>(`${BASE}/parakh-lots/all`, {
      params: plantId ? { plant_id: plantId } : {},
    }).then(r => r.data),

  // QC update — update without po_id in URL
  updateParakhLotQc: (lotId: string, data: Partial<ParakhLot>) =>
    apiClient.patch<ParakhLot>(`${BASE}/parakh-lots/${lotId}`, data).then(r => r.data),

  // Trial samples — standalone ginner samples (no PO)
  listTrialSamples: (plantId?: string) =>
    apiClient.get<ParakhLot[]>(`${BASE}/trial-samples`, {
      params: plantId ? { plant_id: plantId } : {},
    }).then(r => r.data),
  createTrialSample: (data: Record<string, unknown>) =>
    apiClient.post<ParakhLot>(`${BASE}/trial-samples`, data).then(r => r.data),

  // ─── Phase COTTON-PARAKH-001 — evaluate + promote ────────────────────────

  evaluateLot: (lotId: string) =>
    apiClient.get<ParakhEvaluation>(`${BASE}/parakh-lots/${lotId}/evaluate`)
      .then(r => r.data),

  listPromotableTrials: (plantId?: string) =>
    apiClient.get<PromotableTrial[]>(`${BASE}/parakh-lots/promotable-trials`,
      { params: plantId ? { plant_id: plantId } : {} }).then(r => r.data),

  promoteTrial: (trialId: string, body: {
    po_id: string; ginner_lot_no: string; pr_no?: string
  }) =>
    apiClient.post<{ promoted_lot_id: string; po_id: string;
                      ginner_lot_no: string; test_result: string }>(
      `${BASE}/parakh-lots/${trialId}/promote-to-po`, body).then(r => r.data),

  // ─── Phase COTTON-PARAKH-002 — sampling rules + retest ───────────────────

  samplingRules: () =>
    apiClient.get<SamplingRulesByLocation>(`${BASE}/sampling-rules`)
      .then(r => r.data),

  requestRetest: (lotId: string, body: { reason: string; requested_by: string }) =>
    apiClient.post<{ retest_lot_id: string; ginner_lot_no: string;
                      test_result: string; retest_of_lot_id: string }>(
      `${BASE}/parakh-lots/${lotId}/request-retest`, body).then(r => r.data),

  retestHistory: (lotId: string) =>
    apiClient.get<RetestAttempt[]>(
      `${BASE}/parakh-lots/${lotId}/retest-history`).then(r => r.data),
}


// ─── Phase COTTON-PARAKH-001 types ─────────────────────────────────────────

export interface ParakhEvalParameter {
  name: string
  value: number | null
  min_value?: number | null
  max_value?: number | null
  warning_min?: number | null
  warning_max?: number | null
  verdict: 'PASS' | 'FAIL' | 'WARNING' | 'NO_VALUE' | 'NO_THRESHOLD'
  source?: 'PO_CONTRACT' | 'SUPPLIER' | 'DEFAULT'
  message: string
}

export interface ParakhEvaluation {
  lot_id: string
  lot_type: string
  current_test_result: string
  suggested_test_result: 'PASS' | 'FAIL' | 'MARGINAL' | 'INSUFFICIENT_DATA'
  summary: string
  parameters: ParakhEvalParameter[]
  checked_count: number
  missing_count: number
}

export interface PromotableTrial {
  id: string
  trial_ref_no: string | null
  ginner_name: string | null
  ginner_location: string | null
  cotton_variety: string | null
  crop_year: string | null
  total_bales: number | null
  avg_moisture: number | null
  mic_value: number | null
  test_date: string | null
  purchase_recommendation: string | null
  expected_qty_bales: number | null
  price_per_candy_rs: number | null
}

// Phase COTTON-PARAKH-002 — sampling rules + retest types
export interface SamplingRuleInfo {
  default_pct: number
  min_pct: number
  max_pct: number
  allow_extra: boolean
  manual_add_allowed: boolean
}

export interface SamplingRulesByLocation {
  ginner: Record<string, SamplingRuleInfo>  // MOISTURE, HVI
  mill:   Record<string, SamplingRuleInfo>
}

export interface RetestAttempt {
  lot_id: string
  attempt_no: number
  ginner_lot_no: string
  test_result: string
  avg_moisture: number | null
  mic_value: number | null
  test_date: string | null
  retest_reason: string | null
  retest_requested_by: string | null
  retest_requested_at: string | null
  is_latest: boolean
  purchase_recommendation: string | null
}
